import { type ReactNode, useEffect, useId, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Link } from 'react-router-dom'

import { useI18n } from '@/i18n'
import { usePrinciple } from '@/principles'
import type {
    DeclarationStatus,
    Principle,
    PrincipleGroup,
    Scope,
    Verification,
    VerificationDetail,
    VerificationLevel,
} from '@/types'

/**
 * Hover and focus hint that stays out of the way of experienced users. It is
 * positioned against the viewport, so scrolling containers cannot clip it.
 * Set `focusable` unless the child is already inside a link or a button.
 */
export function Tooltip({
    content,
    focusable,
    children,
}: {
    content: ReactNode
    focusable?: boolean
    children: ReactNode
}) {
    const anchor = useRef<HTMLSpanElement>(null)
    const bubble = useRef<HTMLDivElement>(null)
    const [open, setOpen] = useState(false)
    const id = useId()

    useLayoutEffect(() => {
        if (!open || !anchor.current || !bubble.current) {
            return
        }
        const target = anchor.current.getBoundingClientRect()
        const { width, height } = bubble.current.getBoundingClientRect()
        const centred = target.left + target.width / 2 - width / 2
        const below = target.bottom + 6
        const fitsBelow = below + height < window.innerHeight - 8
        bubble.current.style.left = `${Math.max(8, Math.min(centred, window.innerWidth - width - 8))}px`
        bubble.current.style.top = `${fitsBelow ? below : target.top - height - 6}px`
    }, [open])

    useEffect(() => {
        if (!open) {
            return
        }
        const close = () => setOpen(false)
        const onKey = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                close()
            }
        }
        window.addEventListener('scroll', close, true)
        window.addEventListener('keydown', onKey)
        return () => {
            window.removeEventListener('scroll', close, true)
            window.removeEventListener('keydown', onKey)
        }
    }, [open])

    return (
        <span
            ref={anchor}
            className="tip"
            tabIndex={focusable ? 0 : undefined}
            aria-describedby={open ? id : undefined}
            onMouseEnter={() => setOpen(true)}
            onMouseLeave={() => setOpen(false)}
            onFocus={() => setOpen(true)}
            onBlur={() => setOpen(false)}
        >
            {children}
            {open
                ? createPortal(
                      <div ref={bubble} id={id} role="tooltip" className="tip__bubble">
                          {content}
                      </div>,
                      document.body,
                  )
                : null}
        </span>
    )
}

/** Small "?" next to a term: a definition on hover, the glossary on click. */
export function Term({ id }: { id: string }) {
    const { t } = useI18n()
    return (
        <Tooltip content={t(`glossary.${id}.text`)}>
            <Link
                className="term"
                to={`/glossary#${id}`}
                aria-label={`${t('common.explain')} ${t(`glossary.${id}.title`)}`}
            >
                ?
            </Link>
        </Tooltip>
    )
}

export function PrincipleHint({ principle, extra }: { principle: Principle; extra?: string }) {
    const { localized } = useI18n()
    return (
        <>
            <strong>
                {principle.id} · {localized(principle.title)}
            </strong>
            <br />
            {localized(principle.explanation)}
            {extra ? <span className="tip__extra">{extra}</span> : null}
        </>
    )
}

/** Verified beats under review beats self-declared. */
const levelOrder: VerificationLevel[] = ['verified', 'under-review', 'self-declared']

export function strongestVerification<T extends Verification>(items: T[]): T | undefined {
    return [...items].sort((a, b) => levelOrder.indexOf(a.level) - levelOrder.indexOf(b.level))[0]
}

function VerificationIcon({ level }: { level: VerificationLevel }) {
    return (
        <svg className="verify__icon" viewBox="0 0 16 16" aria-hidden="true" focusable="false">
            <circle cx="8" cy="8" r="7" fill="none" stroke="currentColor" strokeWidth="1.6" />
            {level === 'verified' ? (
                <path
                    d="M4.8 8.3l2.2 2.2 4.2-4.6"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.9"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                />
            ) : null}
            {level === 'under-review' ? (
                <path
                    d="M8 4.4V8l2.4 1.8"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                />
            ) : null}
            {level === 'self-declared' ? (
                <circle cx="8" cy="8" r="2.2" fill="currentColor" />
            ) : null}
        </svg>
    )
}

/**
 * Compact "verified by X" marker. `compact` shows only the verifier, with
 * the full sentence in the tooltip — for cards and dense lists.
 */
export function VerificationBadge({
    verification,
    verifier,
    compact,
    extra,
}: {
    verification: Verification
    verifier?: string
    compact?: boolean
    extra?: number
}) {
    const { t, formatDate } = useI18n()
    const name = verifier ?? verification.verifier
    const label = t(`verification.by.${verification.level}`).replace('{name}', name)
    return (
        <span
            className={`verify verify--${verification.level}`}
            title={`${label} · ${formatDate(verification.date)}`}
        >
            <VerificationIcon level={verification.level} />
            {label}
            {extra ? ` +${extra}` : ''}
            {compact ? null : <span className="verify__date">{formatDate(verification.date)}</span>}
        </span>
    )
}

/** The whole verification history of a resource or a profile. */
export function VerificationList({ items }: { items: VerificationDetail[] }) {
    const { t, localized } = useI18n()
    if (!items.length) {
        return <p className="empty">{t('verification.none')}</p>
    }
    return (
        <div className="stack">
            {items.map((item, index) => (
                <div className="usage" key={`${item.verifier}-${index}`}>
                    <div className="usage__head">
                        <VerificationBadge
                            verification={item}
                            verifier={
                                item.verifier_detail
                                    ? localized(item.verifier_detail.name)
                                    : item.verifier
                            }
                        />
                        {item.url ? (
                            <a href={item.url} target="_blank" rel="noreferrer noopener">
                                {t('verification.record')}
                            </a>
                        ) : item.level === 'verified' ? (
                            <span className="item__meta">{t('verification.noEvidence')}</span>
                        ) : null}
                    </div>
                    {item.note ? <p className="declaration__note">{localized(item.note)}</p> : null}
                </div>
            ))}
        </div>
    )
}

/** Page body inside the 1240px container. */
export function PageShell({ children }: { children: ReactNode }) {
    return <div className="shell content__body">{children}</div>
}

/** Full-width teal opening band, as used on the EOSC CZ services. */
export function Hero({
    eyebrow,
    title,
    lead,
    actions,
    art,
}: {
    eyebrow?: string
    title: string
    lead?: string
    actions?: ReactNode
    art?: ReactNode
}) {
    return (
        <section className="hero">
            <div className="shell with-art">
                <div className="with-art__text">
                    {eyebrow ? <p className="hero__eyebrow">{eyebrow}</p> : null}
                    <h1>{title}</h1>
                    {lead ? <p className="hero__lead">{lead}</p> : null}
                    {actions ? <div className="hero__actions">{actions}</div> : null}
                </div>
                {art}
            </div>
        </section>
    )
}

/** Heading block for the inner pages. */
export function PageHead({
    title,
    lead,
    term,
    art,
}: {
    title: string
    lead?: string
    term?: string
    art?: ReactNode
}) {
    return (
        <div className="pagehead with-art">
            <div className="with-art__text">
                <h1>
                    {title} {term ? <Term id={term} /> : null}
                </h1>
                {lead ? <p className="lead">{lead}</p> : null}
            </div>
            {art}
        </div>
    )
}

export function Chip({
    children,
    variant,
    title,
}: {
    children: ReactNode
    variant?: string
    title?: string
}) {
    return (
        <span className={`chip${variant ? ` chip--${variant}` : ''}`} title={title}>
            {children}
        </span>
    )
}

export function StatusChip({ status }: { status: DeclarationStatus }) {
    const { t, statusName } = useI18n()
    return (
        <Tooltip content={t(`glossary.status.${status}`)}>
            <Chip variant={status}>{statusName(status)}</Chip>
        </Tooltip>
    )
}

export function ScopeChip({ scope }: { scope: Scope }) {
    const { t, scopeName } = useI18n()
    return (
        <Tooltip content={t(`glossary.scope.${scope}`)}>
            <Chip variant="outline">{scopeName(scope)}</Chip>
        </Tooltip>
    )
}

export function PrincipleChip({
    id,
    group,
    focusable,
}: {
    id: string
    group?: PrincipleGroup
    focusable?: boolean
}) {
    const principle = usePrinciple(id)
    const derived = (group ?? (id[0] as PrincipleGroup)) || 'F'
    return (
        <Tooltip
            content={principle ? <PrincipleHint principle={principle} /> : id}
            focusable={focusable}
        >
            <span className={`chip chip--principle chip--${derived}`}>{id}</span>
        </Tooltip>
    )
}

function PrincipleDefinition({ id }: { id: string }) {
    const { localized } = useI18n()
    const principle = usePrinciple(id)
    return (
        <div>
            <dt>
                <code>{id}</code> {localized(principle?.title)}
            </dt>
            <dd>{localized(principle?.explanation)}</dd>
        </div>
    )
}

/** Definitions of the given principles, folded away until someone asks. */
export function PrincipleExplainer({ ids }: { ids: string[] }) {
    const { t } = useI18n()
    return (
        <details className="explainer">
            <summary>{t('resource.explain')}</summary>
            <dl className="explainer__list">
                {ids.map((id) => (
                    <PrincipleDefinition key={id} id={id} />
                ))}
            </dl>
            <Link to="/glossary#principles">{t('resource.allPrinciples')} →</Link>
        </details>
    )
}

export function StatCard({
    value,
    label,
    term,
    compact,
}: {
    value: number | string
    label: string
    term?: string
    compact?: boolean
}) {
    return (
        <div className={`card stat${compact ? ' stat--compact' : ''}`}>
            <span className="stat__value">{value}</span>
            <span className="stat__label">
                {label} {term ? <Term id={term} /> : null}
            </span>
        </div>
    )
}

/** Page numbers around the current page, with -1 marking a gap. */
function pageWindow(page: number, count: number): number[] {
    const wanted = [...new Set([1, page - 1, page, page + 1, count])]
        .filter((item) => item >= 1 && item <= count)
        .sort((a, b) => a - b)
    return wanted.flatMap((item, index) =>
        index > 0 && item - wanted[index - 1] > 1 ? [-1, item] : [item],
    )
}

export function Pagination({
    page,
    count,
    onChange,
}: {
    page: number
    count: number
    onChange: (page: number) => void
}) {
    const { t } = useI18n()
    if (count <= 1) {
        return null
    }
    return (
        <nav className="pagination" aria-label={t('common.pagination')}>
            <button
                type="button"
                className="toggle"
                disabled={page <= 1}
                onClick={() => onChange(page - 1)}
            >
                ← {t('common.previous')}
            </button>
            {pageWindow(page, count).map((item, index) =>
                item < 0 ? (
                    <span key={`gap-${index}`} className="pagination__gap">
                        …
                    </span>
                ) : (
                    <button
                        key={item}
                        type="button"
                        className="toggle"
                        aria-pressed={item === page}
                        aria-current={item === page ? 'page' : undefined}
                        onClick={() => onChange(item)}
                    >
                        {item}
                    </button>
                ),
            )}
            <button
                type="button"
                className="toggle"
                disabled={page >= count}
                onClick={() => onChange(page + 1)}
            >
                {t('common.next')} →
            </button>
        </nav>
    )
}

export function Section({
    title,
    hint,
    term,
    action,
    children,
}: {
    title: string
    hint?: string
    term?: string
    action?: ReactNode
    children: ReactNode
}) {
    return (
        <section className="section">
            <div className="section__head">
                <div>
                    <h2>
                        {title} {term ? <Term id={term} /> : null}
                    </h2>
                    {hint ? <p className="section__hint">{hint}</p> : null}
                </div>
                {action}
            </div>
            {children}
        </section>
    )
}

export function Loading() {
    const { t } = useI18n()
    return (
        <p className="state" role="status">
            {t('common.loading')}
        </p>
    )
}

export function ErrorState({ error, onRetry }: { error: Error; onRetry?: () => void }) {
    const { t } = useI18n()
    return (
        <div className="state state--error" role="alert">
            <p>
                <strong>{t('common.error')}</strong>
            </p>
            <p>{t('common.errorHint')}</p>
            <p className="item__meta">{error.message}</p>
            {onRetry ? (
                <button className="button" onClick={onRetry} type="button">
                    {t('common.retry')}
                </button>
            ) : null}
        </div>
    )
}

export function BackLink({ to, label }: { to: string; label: string }) {
    return (
        <Link className="detail__back" to={to}>
            ← {label}
        </Link>
    )
}

export function DefinitionItem({
    term,
    help,
    children,
}: {
    term: string
    help?: string
    children: ReactNode
}) {
    return (
        <div>
            <dt>
                {term} {help ? <Term id={help} /> : null}
            </dt>
            <dd>{children}</dd>
        </div>
    )
}
